import {
  INVOICE_STATUSES_TYPE,
  STATUS_GROUP_TYPES_TYPE,
  BULK_INVOICE_ACTION_TYPE,
  BULK_INVOICE_TYPE_TYPE,
} from "constants/invoicing";
import {
  REVENUE_DATA_SOURCE_TYPES,
  CUSTOMER_SORT_BY_TYPE,
} from "constants/revenueDataSource";
import { stringify } from "qs";
import { Transactions } from "types/Models/books";
import { fileAndFolder } from "types/Models/documents";
import { FileObject } from "types/Models/fileObject";
import { Pagination } from "types/Models/pagination";
import { ItemType } from "types/Models/productAndServices";
import { emptyApi } from "./emptyApi";
import {
  CustomerAddress,
  GroupInvoiceCustomer,
  Invoice,
  InvoiceCustomer,
  InvoiceItem,
  RecurringSettingTypes,
  InvoiceSettingsType,
} from "types/Models/invoice";

export const invoiceApis = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getAllInvoices: build.query<
      {
        invoices: Invoice[];
        current_page: number;
        total_pages: number;
        total_count: number;
        per_page: number;
        paid_amount: number;
        written_off_amount?: number;
        due_amount: number;
        overdue_amount: number;
        total_amount: number;
        excel_document_id?: string;
        last_invoice_transaction_suggestion_run_at?: string;
      },
      {
        groupId: string;
        currentPage?: number;
        entityId: string;
        searchTerm: string | null;
        sortCol?: "INVOICE_DATE" | "DUE_DATE" | "SENT_TO" | "AMOUNT";
        sortOrder?: "ASC" | "DSC";
        startDate?: string;
        endDate?: string;
        sources?: REVENUE_DATA_SOURCE_TYPES[] | null;
        dueDateStartDate?: string;
        dueDateEndDate?: string;
        markedVoidAtStartDate?: string;
        markedVoidAtEndDate?: string;
        markedUncollectibleAtStartDate?: string;
        markedUncollectibleAtEndDate?: string;
        statuses?: INVOICE_STATUSES_TYPE[] | null;
        statusGroup?: STATUS_GROUP_TYPES_TYPE | null;
        customerIds?: string;
        minAmount?: string;
        maxAmount?: string;
        limit?: number;
        linkedStatus?: "UNLINKED" | null;
        generate_report?: boolean;
        export_to_email?: boolean;
        invoiceIds?: string;
        invoiceType?: string;
        customerStates?: string[] | null;
      }
    >({
      query: ({
        groupId,
        entityId,
        currentPage,
        searchTerm,
        sortCol,
        sortOrder,
        startDate,
        endDate,
        dueDateStartDate,
        dueDateEndDate,
        markedVoidAtStartDate,
        markedVoidAtEndDate,
        markedUncollectibleAtStartDate,
        markedUncollectibleAtEndDate,
        statuses,
        statusGroup,
        sources,
        customerIds,
        minAmount,
        maxAmount,
        limit = 20,
        linkedStatus,
        generate_report,
        export_to_email,
        invoiceIds,
        invoiceType,
        customerStates,
      }) => {
        const query = stringify(
          {
            search_term: searchTerm,
            page_num: currentPage,
            sort_col: sortCol,
            sort_order: sortOrder,
            start_date: startDate || null,
            end_date: endDate || null,
            due_date_start: dueDateStartDate || null,
            due_date_end: dueDateEndDate || null,
            marked_void_at_start: markedVoidAtStartDate || null,
            marked_void_at_end: markedVoidAtEndDate || null,
            marked_uncollectible_at_start:
              markedUncollectibleAtStartDate || null,
            marked_uncollectible_at_end: markedUncollectibleAtEndDate || null,
            statuses: statuses?.join(",") || null,
            status_group: statusGroup,
            sources: sources?.join(",") || null,
            customer_uuids: customerIds || null,
            min_amount: minAmount || null,
            max_amount: maxAmount || null,
            limit: limit,
            linked_status: linkedStatus,
            generate_report: generate_report,
            export_to_email,
            ids: invoiceIds || null,
            invoice_type: invoiceType || null,
            customer_state: customerStates?.join(",") || null,
          },
          { skipNulls: true, addQueryPrefix: true }
        );

        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/invoices/${query}`,
        };
      },
      providesTags: (result) => (result ? [{ type: "INVOICES" }] : []),
    }),

    getInvoiceCustomerStates: build.query<
      { states: string[] },
      { groupId: string; entityId: string }
    >({
      query: ({ groupId, entityId }) => ({
        url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/invoices/customer_states/`,
      }),
      providesTags: (result) => (result ? [{ type: "INVOICES" }] : []),
    }),

    createEntityInvoice: build.mutation<
      Invoice,
      {
        groupId: string;
        entityId: string;
        payload?: Omit<
          Invoice,
          | "uuid"
          | "due_date"
          | "invoice_date"
          | "updated_at"
          | "document"
          | "status"
          | "sent_to"
          | "sent_on"
          | "title"
          | "invoice_items"
          | "invoice_number"
          | "invoice_number_without_prefix"
        > & { invoice_items?: Omit<InvoiceItem, "uuid">[] };
      }
    >({
      query: ({ groupId, entityId, payload }) => {
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/invoice/`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: (result) => (result ? [{ type: "INVOICES" }] : []),
    }),

    deleteEntityInvoice: build.mutation<
      Invoice & { invoice_id: string },
      { groupId: string; entityId: string; invoiceId: string; type?: string }
    >({
      query: ({ groupId, entityId, invoiceId, type }) => {
        const query = stringify(
          { type },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/invoice/${invoiceId}/${query}`,
          method: "DELETE",
        };
      },
      invalidatesTags: (result) =>
        result ? [{ type: "INVOICES" }, "NEXUSTRACKER"] : [],
    }),

    getEntityInvoice: build.query<
      Invoice,
      { groupId: string; entityId: string; invoiceId: string; type?: string }
    >({
      query: ({ groupId, entityId, invoiceId, type }) => {
        const query = stringify(
          { type },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/invoice/${invoiceId}/${query}`,
        };
      },
      providesTags: (result) => {
        return result ? [{ type: "INVOICES" }] : [];
      },
    }),

    updateInvoice: build.mutation<
      Invoice,
      {
        groupId: string;
        entityId: string;
        invoiceId: string;
        payload: Partial<Invoice>;
        type?: string;
      }
    >({
      query: ({ groupId, entityId, invoiceId, payload, type }) => {
        const query = stringify(
          { type },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/invoice/${invoiceId}/${query}`,
          method: "PATCH",
          body: payload,
        };
      },
      invalidatesTags: (result) =>
        result
          ? [
              { type: "INVOICES" },
              { type: "INVOICE_CUSTOMERS", id: result.customer?.customer_id },
              "AUDIT_LOG_BY_OBJECT",
              "JOURNAL_ENTRIES",
              "NEXUSTRACKER",
              "TAX_RATES",
            ]
          : [],
    }),
    bulkUpdateInvoice: build.mutation<
      Invoice,
      {
        groupId: string;
        entityId: string;
        record_ids: string[];
        action: BULK_INVOICE_ACTION_TYPE;
        type: BULK_INVOICE_TYPE_TYPE;
      }
    >({
      query: ({ groupId, entityId, ...data }) => {
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/invoices/`,
          method: "PATCH",
          body: data,
        };
      },
      invalidatesTags: (result) =>
        result
          ? [
              { type: "INVOICES" },
              { type: "VENDOR_BILL" },
              "BOOKS_TRANSACTIONS",
              "BOOKS_SINGLE_TRANSACTION",
              "GENERAL_LEDGER",
              "LIVE_REPORT",
              "LINKED_INVOICES",
              "CREDIT_NOTES",
            ]
          : [],
    }),

    sendInvoice: build.mutation<
      Invoice,
      {
        groupId: string;
        entityId: string;
        invoiceId: string;
        payload: {
          attachments?: File[];
          linked_document_ids?: string[];
          body: string;
          subject: string;
          cc_emails?: string;
          from_email_type?: string;
          recipients?: string;
        };
      }
    >({
      query: ({ groupId, entityId, invoiceId, payload }) => {
        const formData = new FormData();

        formData.append("subject", payload.subject);
        formData.append("body", payload.body);
        if (payload.cc_emails) {
          formData.append("cc_emails", payload.cc_emails);
        }

        formData.append("from_email_type", payload.from_email_type || "INKLE");
        payload.attachments?.forEach((file) => {
          formData.append("attachments", file);
        });
        payload.linked_document_ids?.forEach((id) => {
          formData.append("linked_document_ids", id);
        });

        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/invoice/${invoiceId}/send/`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result) => (result ? [{ type: "INVOICES" }] : []),
    }),

    uploadInvoiceAttachment: build.mutation<
      FileObject & { invoice_attachment_id: string },
      { groupId: string; entityId: string; invoiceId: string; file: File }
    >({
      query: ({ groupId, entityId, invoiceId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/invoice/${invoiceId}/attachment/`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result) => (result ? [{ type: "INVOICES" }] : []),
    }),

    deleteInvoiceAttachment: build.mutation<
      void,
      {
        groupId: string;
        entityId: string;
        invoiceId: string;
        attachmentId: string;
      }
    >({
      query: ({ groupId, entityId, invoiceId, attachmentId }) => ({
        url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/invoice/${invoiceId}/attachment/${attachmentId}/`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "INVOICES" }],
    }),

    downloadInvoice: build.query<
      { doc_id: string },
      { groupId: string; entityId: string; invoiceId: string }
    >({
      query: ({ groupId, entityId, invoiceId }) => {
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/invoice/${invoiceId}/download/`,
        };
      },
    }),

    downloadPackingSlip: build.query<
      { doc_id: string },
      { groupId: string; entityId: string; invoiceId: string }
    >({
      query: ({ groupId, entityId, invoiceId }) => {
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/invoice/${invoiceId}/packing_slip/`,
        };
      },
    }),

    getAllInvoiceCustomers: build.query<
      Pagination & { recipients: InvoiceCustomer[] },
      {
        groupId: string;
        entityId: string;
        searchTerm?: string;
        customerId?: string;
        pageNum?: string | number;
      }
    >({
      query: ({ groupId, entityId, searchTerm, customerId, pageNum }) => {
        const query = stringify(
          {
            search_term: searchTerm || null,
            customer_id: customerId || null,
            page_num: pageNum || null,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/recipients/${query}`;
      },
      providesTags: (result) =>
        result
          ? [
              { type: "INVOICE_CUSTOMERS" as const, id: "LIST" },
              ...result.recipients.map((c) => ({
                type: "INVOICE_CUSTOMERS" as const,
                id: c.uuid,
              })),
            ]
          : [],
    }),

    getInfiniteInvoiceCustomers: build.infiniteQuery<
      Pagination & { recipients: InvoiceCustomer[] },
      { groupId: string; entityId: string; search_term?: string },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
          if (lastPageParam < (lastPage.total_pages || 1)) {
            return lastPageParam + 1;
          }
          return undefined;
        },
        getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
          return firstPageParam > 1 ? firstPageParam - 1 : undefined;
        },
      },
      query({ queryArg, pageParam }) {
        const { groupId, entityId, search_term } = queryArg;
        const query = stringify(
          { search_term: search_term || null, page_num: pageParam },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/recipients/${query}`,
        };
      },
      providesTags: (result) =>
        result ? [{ type: "INVOICE_CUSTOMERS" as const, id: "LIST" }] : [],
    }),

    getAllInvoiceCustomersList: build.query<
      Pagination & {
        client_customers: GroupInvoiceCustomer[];
        customers_missing_address_count: number;
        customers_missing_address: GroupInvoiceCustomer[];
      },
      {
        groupId: string;
        entityId: string;
        pageNum: string | number;
        searchTerm?: string | null;
        sources?: string;
        sortBy?: CUSTOMER_SORT_BY_TYPE;
        sortOrder?: "ASC" | "DESC";
        minAmountDue?: string;
        maxAmountDue?: string;
        minAmountPaid?: string;
        maxAmountPaid?: string;
        minTotalInvoiced?: string;
        maxTotalInvoiced?: string;
        hasOverdueInvoices?: boolean;
        hasNoOutstandingDues?: boolean;
        hasNoPayments?: boolean;
      }
    >({
      query: ({
        groupId,
        entityId,
        pageNum,
        searchTerm,
        sources,
        sortBy,
        sortOrder,
        minAmountDue,
        maxAmountDue,
        minAmountPaid,
        maxAmountPaid,
        minTotalInvoiced,
        maxTotalInvoiced,
        hasOverdueInvoices,
        hasNoOutstandingDues,
        hasNoPayments,
      }) => {
        const query = stringify(
          {
            page_num: pageNum,
            search_term: searchTerm || null,
            sources: sources || null,
            sort_by: sortBy || null,
            sort_order: sortOrder || null,
            min_amount_due: minAmountDue || null,
            max_amount_due: maxAmountDue || null,
            min_amount_paid: minAmountPaid || null,
            max_amount_paid: maxAmountPaid || null,
            min_total_invoiced: minTotalInvoiced || null,
            max_total_invoiced: maxTotalInvoiced || null,
            has_overdue_invoices: hasOverdueInvoices || null,
            has_no_outstanding_dues: hasNoOutstandingDues || null,
            has_no_payments: hasNoPayments || null,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/customers/${query}`,
        };
      },
      providesTags: (result) =>
        result
          ? [
              { type: "INVOICE_CUSTOMERS" as const, id: "LIST" },
              ...result.client_customers?.map((c) => ({
                type: "INVOICE_CUSTOMERS" as const,
                id: c.uuid,
              })),
            ]
          : [],
    }),

    getInvoiceCustomer: build.query<
      {
        uuid: string;
        name: string;
        email: string;
        phone: string;
        billing_address: string;
        due_amount: number;
        stripe_due_amount: number;
        total_amount: number;
        total_stripe_amount: number;
        paid_amount: number;
        paid_stripe_amount: number;
        percent_paid: number;
        stripe_percent_paid: number;
        total_percent_paid: number;
        invoices: Invoice[];
        is_reminders_enabled?: boolean;
        source: REVENUE_DATA_SOURCE_TYPES;
        recipient_emails?: string[];
        company_name: string;
        billing_address_id: string;
        shipping_address_id: string;
        shipping_same_as_billing: boolean;
        currency: string;
        customer_prefix?: string;
        cc_recipient_emails?: string[];
      },
      {
        groupId: string;
        entityId: string;
        customerId: string;
        sortCol?: "INVOICE_NUMBER" | "INVOICE_DATE";
        sortOrder?: "ASC" | "DSC";
      }
    >({
      query: ({ groupId, entityId, customerId, sortCol, sortOrder }) => {
        const query = stringify(
          {
            sort_col: sortCol,
            sort_order: sortOrder,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/customers/${customerId}/${query}`,
          method: "GET",
        };
      },
      providesTags: (result) =>
        result ? [{ type: "INVOICE_CUSTOMERS", id: result.uuid }] : [],
    }),

    deleteCustomer: build.mutation<
      GroupInvoiceCustomer,
      { groupId: string; entityId: string; customerId: string }
    >({
      query: ({ groupId, entityId, customerId }) => {
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/customers/${customerId}/`,
          method: "DELETE",
        };
      },
      invalidatesTags: (result) =>
        result ? [{ type: "INVOICE_CUSTOMERS", id: result.uuid }] : [],
    }),

    bulkDeleteCustomers: build.mutation<
      { deleted_customer_ids: string[]; skipped_customer_ids: string[] },
      { groupId: string; entityId: string; customerIds: string[] }
    >({
      query: ({ groupId, entityId, customerIds }) => ({
        url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/customers/bulk/`,
        method: "DELETE",
        body: { customer_ids: customerIds },
      }),
      invalidatesTags: [{ type: "INVOICE_CUSTOMERS", id: "LIST" }],
    }),

    exportOutstandingInvoices: build.query<
      { message?: string; excel_document_id?: string; file_name?: string },
      {
        groupId: string;
        entityId: string;
        customerId: string;
        exportToEmail: boolean;
      }
    >({
      query: ({ groupId, entityId, customerId, exportToEmail }) => ({
        url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/customers/${customerId}/export_outstanding/?export_to_email=${exportToEmail}`,
        method: "GET",
      }),
    }),

    createInvoiceCustomer: build.mutation<
      InvoiceCustomer,
      {
        groupId: string;
        entityId: string;
        payload: Omit<
          InvoiceCustomer,
          | "customer_id"
          | "is_reminders_enabled"
          | "billing_address"
          | "shipping_address"
          | "shipping_same_as_billing"
          | "billing_address_id"
          | "shipping_address_id"
        >;
      }
    >({
      query: ({ groupId, entityId, payload }) => {
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/recipients/`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: (result) =>
        result ? [{ type: "INVOICE_CUSTOMERS", id: "LIST" }] : [],
    }),

    updateInvoiceCustomer: build.mutation<
      InvoiceCustomer,
      {
        groupId: string;
        entityId: string;
        customerId: string;
        payload: Partial<InvoiceCustomer>;
      }
    >({
      query: ({ groupId, entityId, customerId, payload }) => {
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/recipients/${customerId}/`,
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: (result) =>
        result
          ? [
              { type: "INVOICE_CUSTOMERS", id: result.uuid },
              { type: "INVOICES" },
              { type: "INVOICE_SETTING" },
            ]
          : [],
    }),

    createOrUpdateInvoiceSettings: build.mutation<
      InvoiceSettingsType & { logo: File },
      {
        groupId: string;
        entityId: string;
        payload: Partial<Omit<InvoiceSettingsType, "address">> & {
          autofill_address_id?: string;
          logo?: File;
        };
      }
    >({
      query: ({ groupId, entityId, payload }) => {
        const formData = new FormData();

        Object.entries(payload).forEach(([key, value]) => {
          if (!value) return;

          if (Array.isArray(value)) {
            value.forEach((item) => formData.append(key, item));
          } else {
            formData.append(key, value as string | Blob);
          }
        });

        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/entity_info/`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result) => {
        return result ? [{ type: "INVOICE_SETTING" }] : [];
      },
    }),

    getInvoiceSettings: build.query<
      InvoiceSettingsType | null,
      {
        groupId: string;
        entityId: string;
      }
    >({
      query: ({ groupId, entityId }) => {
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/entity_info/`,
        };
      },
      providesTags: (result) =>
        result || result === null ? [{ type: "INVOICE_SETTING" }] : [],
    }),

    getPendingDues: build.query<void, { groupId: string }>({
      query: ({ groupId }) => ({
        url: `/api/inkle/company/group/${groupId}/pending_dues/`,
      }),
    }),
    createInvoicingEmailConfiguration: build.mutation<
      null,
      {
        payload: {
          subject: string;
          cc_emails: string;
          greeting: string;
          body: string;
          signature: string;
          payment_info: string;
          reminder_setting_types: string;
        };
        groupId: string;
        entityId: string;
      }
    >({
      query: ({ payload, groupId, entityId }) => {
        return {
          url: `/api/inkle/invoicing/reminders/group/${groupId}/entity/${entityId}/`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ["INVOICE_SETTING"],
    }),
    updateInvoicingEmailConfiguration: build.mutation<
      null,
      {
        payload: {
          subject?: string;
          cc_emails?: string;
          greeting?: string;
          body?: string;
          signature?: string;
          payment_info?: string;
          reminder_setting_types?: string;
          is_reminders_enabled?: boolean;
        };
        reminderId: string;
        groupId: string;
        entityId: string;
      }
    >({
      query: ({ payload, reminderId, groupId, entityId }) => {
        return {
          url: `/api/inkle/invoicing/reminders/group/${groupId}/entity/${entityId}/reminder_setting/${reminderId}`,
          method: "PATCH",
          body: payload,
        };
      },
      invalidatesTags: ["INVOICE_SETTING"],
    }),
    createInvoiceRecurring: build.mutation<
      null,
      {
        payload: {
          is_recurring_enabled: boolean;
          recurring_setting_type?: RecurringSettingTypes;
          recurring_end_date?: string | null;
          is_recurring_send_automated?: boolean;
        };
        invoiceId: string;
        groupId: string;
        entityId: string;
      }
    >({
      query: ({ payload, invoiceId, groupId, entityId }) => {
        return {
          url: `/api/inkle/invoicing/invoice_settings/group/${groupId}/entity/${entityId}/invoice/${invoiceId}/`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ["INVOICES"],
    }),

    updateInvoiceRecurring: build.mutation<
      null,
      {
        payload: {
          is_recurring_enabled: boolean;
          recurring_setting_type?: RecurringSettingTypes;
          recurring_end_date?: string | null;
          is_recurring_send_automated?: boolean;
        };
        invoiceSettingId: string;
        groupId: string;
        entityId: string;
        invoiceId: string;
      }
    >({
      query: ({ payload, invoiceSettingId, groupId, entityId, invoiceId }) => {
        return {
          url: `/api/inkle/invoicing/invoice_settings/group/${groupId}/entity/${entityId}/invoice/${invoiceId}/invoice_setting/${invoiceSettingId}/`,
          method: "PATCH",
          body: payload,
        };
      },
      invalidatesTags: ["INVOICES"],
    }),

    markInvoicePaid: build.mutation<
      void,
      {
        entityId: string;
        invoiceId: string;
        transactionIds: string[];
        creditNoteIds?: string[];
        addedTransactions?: {
          amount: number;
          description: string;
          category_id: string;
          merchant_id: string;
        }[];
        writeOffRemainingAt?: string;
      }
    >({
      query: ({
        entityId,
        invoiceId,
        transactionIds,
        creditNoteIds,
        addedTransactions,
        writeOffRemainingAt,
      }) => {
        return {
          url: `/api/inkle/invoicing/entity/${entityId}/invoice/${invoiceId}/paid/`,
          method: "PUT",
          body: {
            txn_ids: transactionIds,
            linked_credit_debit_note_ids: creditNoteIds,
            split_transactions: addedTransactions,
            write_off_remaining_at: writeOffRemainingAt,
          },
        };
      },
      invalidatesTags: [
        "BOOKS_TRANSACTIONS",
        "BOOKS_SINGLE_TRANSACTION",
        "GENERAL_LEDGER",
        "LIVE_REPORT",
        "LINKED_INVOICES",
        "INVOICES",
        "CREDIT_NOTES",
      ],
    }),

    writeOffInvoicePendingAmount: build.mutation<
      Invoice,
      { entityId: string; invoiceId: string; writtenOffAt: string }
    >({
      query: ({ entityId, invoiceId, writtenOffAt }) => ({
        url: `/api/inkle/invoicing/entity/${entityId}/invoice/${invoiceId}/write-off/`,
        method: "PUT",
        body: { written_off_at: writtenOffAt },
      }),
      invalidatesTags: (result) => [
        "INVOICES",
        "GENERAL_LEDGER",
        "LIVE_REPORT",
        "JOURNAL_ENTRIES",
        "AUDIT_LOG_BY_OBJECT",
        { type: "INVOICE_CUSTOMERS", id: result?.customer?.customer_id },
      ],
    }),

    resyncInvoice: build.mutation<
      Invoice,
      { entityId: string; invoiceId: string }
    >({
      query: ({ entityId, invoiceId }) => ({
        url: `/api/inkle/invoicing/entity/${entityId}/invoice/${invoiceId}/resync/`,
        method: "POST",
      }),
      invalidatesTags: (result) => [
        "INVOICES",
        "INVOICE_SOURCE_SYNC",
        "GENERAL_LEDGER",
        "LIVE_REPORT",
        "JOURNAL_ENTRIES",
        "AUDIT_LOG_BY_OBJECT",
        { type: "INVOICE_CUSTOMERS", id: result?.customer?.customer_id },
      ],
    }),

    getPaidInvoiceTransactions: build.query<
      { transactions: Transactions[]; invoices: Invoice[] },
      { entityId: string; invoiceId: string }
    >({
      query: ({ entityId, invoiceId }) => {
        return {
          url: `/api/inkle/invoicing/entity/${entityId}/invoice/${invoiceId}/paid/`,
        };
      },
    }),

    getAllCustomerAddresses: build.query<
      CustomerAddress[],
      {
        entityId: string;
        customerId: string;
        addressType: "BILLING" | "SHIPPING";
      }
    >({
      query: ({ entityId, customerId, addressType }) => {
        const query = stringify(
          { address_type: addressType },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/invoicing/entity/${entityId}/recipients/${customerId}/address/${query}`,
        };
      },
      providesTags: ["CUSTOMER_ADDRESSES"],
    }),

    getCustomerAddress: build.query<
      CustomerAddress,
      {
        entityId: string;
        customerId: string;
        addressId: string;
      }
    >({
      query: ({ entityId, customerId, addressId }) => {
        return {
          url: `/api/inkle/invoicing/entity/${entityId}/recipients/${customerId}/address/${addressId}/`,
        };
      },
      providesTags: ["INVOICE_CUSTOMER_ADDRESS"],
    }),

    createCustomerAddress: build.mutation<
      CustomerAddress,
      {
        entityId: string;
        customerId: string;
        payload: { address: CustomerAddress };
      }
    >({
      query: ({ entityId, customerId, payload }) => {
        return {
          url: `/api/inkle/invoicing/entity/${entityId}/recipients/${customerId}/create_address/`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ["CUSTOMER_ADDRESSES"],
    }),

    updateCustomerAddress: build.mutation<
      CustomerAddress,
      {
        entityId: string;
        customerId: string;
        addressId: string;
        payload: { address: CustomerAddress };
      }
    >({
      query: ({ entityId, customerId, addressId, payload }) => {
        return {
          url: `/api/inkle/invoicing/entity/${entityId}/recipients/${customerId}/address/${addressId}/`,
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: ["CUSTOMER_ADDRESSES"],
    }),

    genrateShortLink: build.mutation<
      { token: string },
      {
        entityId: string;
        invoiceId: string;
        from: "invoice_edit_screen" | "mail_draft_screen";
      }
    >({
      query: ({ entityId, invoiceId, from }) => {
        return {
          url: `/api/inkle/invoicing/entity/${entityId}/generate_short_link_token/`,
          method: "POST",
          body: {
            invoice_id: invoiceId,
            from,
          },
        };
      },
    }),

    getPublicInvoice: build.query<Invoice, { token: string }>({
      query: ({ token }) => {
        const query = stringify(
          { token },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/invoicing/webpage_details/${query}`,
        };
      },
    }),

    uploadBulkInvoice: build.mutation<
      {
        bulk_file_id: string;
      },
      {
        entityId: string;
        file: File;
      }
    >({
      query: ({ entityId, file }) => {
        const formData = new FormData();
        formData.append("excel", file);

        return {
          url: `/api/inkle/invoicing/entity/${entityId}/bulk_xlsx/`,
          method: "POST",
          body: formData,
        };
      },
    }),
    populateBulkInvoice: build.mutation<
      fileAndFolder[],
      {
        entityId: string;
        bulkFileId: string;
      }
    >({
      query: ({ entityId, bulkFileId }) => {
        return {
          url: `/api/inkle/invoicing/entity/${entityId}/populate_bulk_xlsx/`,
          method: "POST",
          body: { bulk_file_id: bulkFileId },
        };
      },
      invalidatesTags: ["INVOICES"],
    }),

    getCurrencies: build.query<Record<string, string>, void>({
      query: () => ({
        url: `/api/inkle/invoicing/currencies/`,
      }),
    }),

    getExchangeRate: build.query<
      { exchange_rate: number; base: string; target: string; date: string },
      { base_currency: string; target_currency: string; date: string }
    >({
      query: ({ base_currency, target_currency, date }) => ({
        url: `/api/inkle/invoicing/currencies/get_exchange_rate/`,
        params: {
          base_currency,
          target_currency,
          date,
        },
      }),
    }),

    unlinkTransactions: build.mutation<
      //Bulk unlink of transaction on the basis of invoice
      void,
      {
        entityId: string;
        invoiceId: string;
        financial_record_type: string;
        groupId: string;
        transaction_id?: string;
      }
    >({
      query: ({
        entityId,
        invoiceId,
        financial_record_type,
        groupId,
        transaction_id,
      }) => {
        const body = transaction_id
          ? { transaction_id, financial_record_type }
          : { financial_record_type };

        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/invoice/${invoiceId}/unlink/`,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: () => [
        "BOOKS_TRANSACTIONS",
        "BOOKS_SINGLE_TRANSACTION",
        "GENERAL_LEDGER",
        "LIVE_REPORT",
        "VENDOR_BILL",
        "INVOICES",
        "LINKED_INVOICES",
      ],
    }),
    getAuthorizationURLGmail: build.query<
      { authorization_url: string },
      {
        callbackUrl: string;
        entityId?: string;
        source: "STAKEHOLDER" | "INVOICE";
      }
    >({
      query: ({ callbackUrl, source, entityId }) => ({
        url: `/api/inkle/users/auth/google/email/`,
        params: {
          callback_url: callbackUrl,
          source,
          entity_id: entityId,
        },
      }),
    }),
    suggestTransactions: build.mutation<
      void,
      {
        groupId: string;
        entityId: string;
      }
    >({
      query: ({ groupId, entityId }) => {
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/suggest-transactions/`,
          method: "POST",
        };
      },
      invalidatesTags: ["INVOICES", "BOOKS_TRANSACTIONS"],
    }),

    getCustomerDefaultPrices: build.query<
      {
        uuid: string;
        customer_id: string;
        product_id: string;
        product_name: string;
        product_type: ItemType;
        product_category_name: string;
        product_price: {
          uuid: string;
          price: number;
          description: string;
          frequency: string;
        };
      }[],
      {
        entityId: string;
        customerId: string;
      }
    >({
      query: ({ entityId, customerId }) => ({
        url: `/api/inkle/invoicing/entity/${entityId}/customers/${customerId}/product_prices/`,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ uuid }) => ({
                type: "CUSTOMER_DEFAULT_PRICES" as const,
                id: uuid,
              })),
              { type: "CUSTOMER_DEFAULT_PRICES" as const, id: "LIST" },
            ]
          : [],
    }),

    addCustomerDefaultPrice: build.mutation<
      void,
      {
        entityId: string;
        customerId: string;
        priceId: string;
        productId: string;
      }
    >({
      query: ({ entityId, customerId, priceId, productId }) => ({
        url: `/api/inkle/invoicing/entity/${entityId}/customers/${customerId}/product_prices/`,
        method: "POST",
        body: {
          price_id: priceId,
          product_id: productId,
        },
      }),
      invalidatesTags: [{ type: "CUSTOMER_DEFAULT_PRICES", id: "LIST" }],
    }),

    removeCustomerDefaultPrice: build.mutation<
      void,
      {
        entityId: string;
        customerId: string;
        priceId: string;
        productId: string;
      }
    >({
      query: ({ entityId, customerId, priceId, productId }) => ({
        url: `/api/inkle/invoicing/entity/${entityId}/customers/${customerId}/product_prices/`,
        method: "DELETE",
        body: {
          price_id: priceId,
          product_id: productId,
        },
      }),
      invalidatesTags: [{ type: "CUSTOMER_DEFAULT_PRICES", id: "LIST" }],
    }),

    updateCustomExchangeRate: build.mutation<
      Invoice,
      {
        entityId: string;
        invoiceId: string;
        exchangeRate: number;
      }
    >({
      query: ({ entityId, invoiceId, exchangeRate }) => ({
        url: `/api/inkle/invoicing/entity/${entityId}/invoice/${invoiceId}/exchange_rate/`,
        method: "PATCH",
        body: {
          custom_exchange_rate: exchangeRate,
        },
      }),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: "INVOICES", id: invoiceId },
        "INVOICES",
      ],
    }),

    resetCustomExchangeRate: build.mutation<
      Invoice,
      {
        entityId: string;
        invoiceId: string;
      }
    >({
      query: ({ entityId, invoiceId }) => ({
        url: `/api/inkle/invoicing/entity/${entityId}/invoice/${invoiceId}/exchange_rate/`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: "INVOICES", id: invoiceId },
        "INVOICES",
      ],
    }),

    extractInvoiceFromImage: build.mutation<
      Invoice,
      {
        groupId: string;
        entityId: string;
        files: File[];
        invoiceId?: string;
      }
    >({
      query: ({ groupId, entityId, files, invoiceId }) => {
        const formData = new FormData();
        files.forEach((file) => {
          formData.append("files", file);
        });
        if (invoiceId) {
          formData.append("invoice_id", invoiceId);
        }
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/extract_invoice/`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result) => (result ? [{ type: "INVOICES" }] : []),
    }),
  }),
  overrideExisting: false,
});

export const {
  useLazyGetPendingDuesQuery,
  useGetAllInvoicesQuery,
  useLazyGetAllInvoicesQuery,
  useGetInvoiceCustomerStatesQuery,
  useGetEntityInvoiceQuery,
  useCreateEntityInvoiceMutation,
  useUpdateInvoiceMutation,
  useBulkUpdateInvoiceMutation,
  useGetAllInvoiceCustomersQuery,
  useGetInfiniteInvoiceCustomersInfiniteQuery,
  useCreateInvoiceCustomerMutation,
  useUpdateInvoiceCustomerMutation,
  useGetInvoiceSettingsQuery,
  useCreateOrUpdateInvoiceSettingsMutation,
  useSendInvoiceMutation,
  useLazyDownloadInvoiceQuery,
  useDownloadInvoiceQuery,
  useLazyDownloadPackingSlipQuery,
  useGetAllInvoiceCustomersListQuery,
  useGetInvoiceCustomerQuery,
  useDeleteEntityInvoiceMutation,
  useLazyGetEntityInvoiceQuery,
  useCreateInvoicingEmailConfigurationMutation,
  useUpdateInvoicingEmailConfigurationMutation,
  useCreateInvoiceRecurringMutation,
  useUpdateInvoiceRecurringMutation,
  useDeleteCustomerMutation,
  useBulkDeleteCustomersMutation,
  useLazyGetAllInvoiceCustomersListQuery,
  useMarkInvoicePaidMutation,
  useWriteOffInvoicePendingAmountMutation,
  useResyncInvoiceMutation,
  useGetPaidInvoiceTransactionsQuery,
  useGetAllCustomerAddressesQuery,
  useGetCustomerAddressQuery,
  useCreateCustomerAddressMutation,
  useUpdateCustomerAddressMutation,
  useGenrateShortLinkMutation,
  useGetPublicInvoiceQuery,
  useUploadBulkInvoiceMutation,
  usePopulateBulkInvoiceMutation,
  useGetCurrenciesQuery,
  useGetExchangeRateQuery,
  useUnlinkTransactionsMutation,
  useLazyGetAuthorizationURLGmailQuery,
  useSuggestTransactionsMutation,
  useGetCustomerDefaultPricesQuery,
  useAddCustomerDefaultPriceMutation,
  useRemoveCustomerDefaultPriceMutation,
  useUpdateCustomExchangeRateMutation,
  useResetCustomExchangeRateMutation,
  useUploadInvoiceAttachmentMutation,
  useDeleteInvoiceAttachmentMutation,
  useExtractInvoiceFromImageMutation,
  useLazyExportOutstandingInvoicesQuery,
} = invoiceApis;
